import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Snackbar,
    Alert,
    alpha,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    Add,
    Remove,
    ArrowBack,
    CloudUpload,
    Delete,
    InfoOutlined,
    Download
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import { TABLE_FONT_FAMILY } from '../theme';

import { authService } from '../services/authService';
import { useDocumentEncryption } from '../contexts/DocumentEncryptionContext';
import { CryptoUtils } from '../utils/cryptoUtils';
import { documentsService } from '../services/documentsService';
import { openFilePreviewOrDownload } from '../utils/filePreview';
import LoadingAnimation from '../components/LoadingAnimation';
import PasswordVerificationModal from '../components/PasswordVerificationModal';
import UserAvatar from '../components/UserAvatar';
import { useHRAccess } from '../hooks/useHRAccess';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
const AWAITING_TOOLTIP =
    'Days requested (pending or approved) that are not yet subtracted from the balance. They are posted on the 28th of each month, together with the monthly credit. Leave after the 28th waits until the following 28th.';

interface UserDetails {
    id: number;
    name: string;
    mail: string;
    department: number;
    paid_leave_per_month: number;
    weekly_hours?: number;
    contract_type: number;
    user_office: number;
    birthday?: string;
    profile_picture?: string;
}

interface HolidayBalance {
    category: string;
    balance: number;
}

interface Transaction {
    id: number;
    date: string;
    type: string;
    days: number;
    balance_after: number;
    notes: string;
    created_by: string;
    created_at: string | null;
    export_date: string | null;
    in_payroll_period: boolean;
}

interface UserDocument {
    id: number;
    title: string;
    document_type: string;
    uploaded_at: string;
    original_filename: string;
}

const CUTOFF_DAY = 28;

const categoryLabel = (category: string) => {
    if (category === 'rr') return 'R&R';
    return category.replace(/_/g, ' ');
};

const nextHolidayCutoff = (from = new Date()) => {
    const year = from.getFullYear();
    const month = from.getMonth();
    const thisMonthCutoff = new Date(year, month, CUTOFF_DAY);
    const today = new Date(year, month, from.getDate());
    if (today < thisMonthCutoff) return thisMonthCutoff;
    return new Date(year, month + 1, CUTOFF_DAY);
};

const sectionLabelSx = (isDark: boolean) => ({
    fontWeight: 600,
    fontSize: '0.7rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: isDark ? '#b0bec5' : '#546e7a'
});

const outlinedButtonSx = (isDark: boolean, panelBorder: string) => ({
    borderRadius: 1,
    borderColor: panelBorder,
    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontWeight: 600,
    fontSize: '0.72rem',
    boxShadow: 'none'
});

const sandButtonSx = {
    borderRadius: 1,
    fontWeight: 600,
    boxShadow: 'none',
    bgcolor: NAUTILUS.sand,
    color: NAUTILUS.deepOcean,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontSize: '0.72rem',
    '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' }
};

const tableHeadCellSx = (isDark: boolean, surfaceBg: string, panelBorder: string) => ({
    position: 'sticky' as const,
    top: 0,
    bgcolor: surfaceBg,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontWeight: 600,
    fontSize: '0.7rem',
    color: isDark ? '#b0bec5' : '#546e7a',
    borderBottom: `1px solid ${panelBorder}`,
    fontFamily: TABLE_FONT_FAMILY,
    whiteSpace: 'nowrap'
});

const HRUserDetailsPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { ensureUnlocked } = useDocumentEncryption();
    const { isVerified: passwordVerified, grantAccess } = useHRAccess();

    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
    const muted = isDark ? '#b0bec5' : '#546e7a';
    const ink = isDark ? '#ffffff' : NAUTILUS.deepOcean;

    const [passwordVerificationOpen, setPasswordVerificationOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<UserDetails | null>(null);
    const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
    const [balances, setBalances] = useState<HolidayBalance[]>([]);
    const [leaveAwaitingDeduction, setLeaveAwaitingDeduction] = useState<{ paid_leave: number; rr: number }>({
        paid_leave: 0,
        rr: 0
    });
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [historyScope, setHistoryScope] = useState<'in_payroll_period' | 'all' | 'recent'>('in_payroll_period');
    const [documents, setDocuments] = useState<UserDocument[]>([]);

    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [actionType, setActionType] = useState<'add' | 'deduct' | 'adjust'>('add');
    const [actionData, setActionData] = useState({
        days: '',
        balance: '',
        reason: '',
        category: 'paid_leave',
        date: new Date().toISOString().split('T')[0]
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
    const [uploading, setUploading] = useState(false);

    const onDrop = async (acceptedFiles: File[]) => {
        if (!userId) return;
        setUploading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            const key = await ensureUnlocked();

            for (const file of acceptedFiles) {
                try {
                    const fileArrayBuffer = await file.arrayBuffer();
                    const encryptedArrayBuffer = await CryptoUtils.encryptFile(fileArrayBuffer, key);
                    const encryptedBlob = new Blob([encryptedArrayBuffer], { type: 'application/octet-stream' });

                    const formData = new FormData();
                    formData.append('document', encryptedBlob, file.name);
                    formData.append('user_id', userId);
                    formData.append('document_type', 'other');
                    formData.append('title', file.name.split('.')[0]);

                    const response = await documentsService.uploadDocument(formData);

                    if (!response.error) {
                        successCount++;
                    } else {
                        failCount++;
                    }
                } catch (error) {
                    console.error('Upload error for file:', file.name, error);
                    failCount++;
                }
            }
        } catch (error) {
            console.error('Encryption unlock failed:', error);
            setSnackbar({ open: true, message: 'Encryption key required to upload documents', severity: 'error' });
            setUploading(false);
            return;
        }

        setUploading(false);
        if (successCount > 0) {
            setSnackbar({ open: true, message: `Successfully uploaded ${successCount} files`, severity: 'success' });
            fetchData();
        }
        if (failCount > 0) {
            setSnackbar({ open: true, message: `Failed to upload ${failCount} files`, severity: 'error' });
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const handleDownloadDocument = async (docId: number, originalFilename: string) => {
        try {
            const key = await ensureUnlocked();

            const response = await fetch(`/api/hr/documents/${docId}/download`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            });

            if (!response.ok) throw new Error('Download failed');

            const contentType = response.headers.get('content-type');
            let encryptedBlob: Blob;

            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                if (data.download_url) {
                    const s3Res = await fetch(data.download_url);
                    encryptedBlob = await s3Res.blob();
                } else {
                    throw new Error('S3 URL not found');
                }
            } else {
                encryptedBlob = await response.blob();
            }

            const encryptedArrayBuffer = await encryptedBlob.arrayBuffer();
            const decryptedArrayBuffer = await CryptoUtils.decryptFile(encryptedArrayBuffer, key);

            const blob = new Blob([decryptedArrayBuffer], { type: 'application/octet-stream' });
            openFilePreviewOrDownload(blob, originalFilename);
        } catch (error) {
            console.error('Download/Decryption failed:', error);
            setSnackbar({ open: true, message: 'Failed to download or decrypt document. Ensure you have the correct key.', severity: 'error' });
        }
    };

    const handleDeleteDocument = async (docId: number) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;

        try {
            const response = await fetch(`/api/hr/documents/${docId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            });

            if (!response.ok) throw new Error('Delete failed');

            setSnackbar({ open: true, message: 'Document deleted successfully', severity: 'success' });
            fetchData();
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to delete document', severity: 'error' });
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('access_token');
            const headers = { 'Authorization': `Bearer ${token}` };

            const deptsRes = await fetch('/api/hr/departments', { headers });
            const deptsData = await deptsRes.json();
            setDepartments(deptsData.departments || []);

            const userRes = await fetch(`/api/auth/users/${userId}`, { headers });
            const userData = await userRes.json();
            setUser(userData.user);

            const holidayRes = await fetch(`/api/hr/employees/${userId}/holidays`, { headers });
            const holidayData = await holidayRes.json();
            setTransactions(holidayData.transactions || []);

            const balanceList = Object.entries(holidayData.balances || {}).map(([category, balance]) => ({
                category,
                balance: balance as number
            }));

            if (!balanceList.some((b) => b.category === 'paid_leave')) {
                balanceList.unshift({ category: 'paid_leave', balance: holidayData.current_balance || 0 });
            }

            setBalances(balanceList);
            setLeaveAwaitingDeduction({
                paid_leave: holidayData.leave_awaiting_deduction?.paid_leave || 0,
                rr: holidayData.leave_awaiting_deduction?.rr || 0
            });

            const docsRes = await fetch(`/api/hr/documents?user_id=${userId}`, { headers });
            const docsData = await docsRes.json();
            setDocuments(docsData.documents || []);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setSnackbar({ open: true, message: 'Failed to load user data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!passwordVerified) setPasswordVerificationOpen(true);
    }, [passwordVerified]);

    useEffect(() => {
        if (userId && passwordVerified) {
            fetchData();
        }
    }, [userId, passwordVerified]);

    const filteredTransactions = transactions.filter((transaction) => {
        if (historyScope === 'in_payroll_period') {
            return transaction.in_payroll_period;
        }
        if (historyScope === 'recent') {
            if (!transaction.created_at) return false;
            const createdAtDate = new Date(transaction.created_at);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return createdAtDate >= thirtyDaysAgo;
        }
        return true;
    });

    const handleUpdateProfile = async (updates: Partial<UserDetails>) => {
        try {
            const response = await fetch(`/api/auth/users/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify(updates)
            });

            if (response.ok) {
                setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
                setUser(prev => prev ? { ...prev, ...updates } : null);
            } else {
                const data = await response.json();
                throw new Error(data.error || 'Update failed');
            }
        } catch (error: any) {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
            fetchData();
        }
    };

    const handleHolidayAction = async () => {
        try {
            let endpoint = '';
            const payload: Record<string, unknown> = {
                reason: actionData.reason,
                date: actionData.date,
                category: actionData.category
            };

            if (actionType === 'add') {
                endpoint = `/api/hr/employees/${userId}/holidays/add`;
                payload.days = parseFloat(actionData.days);
            } else if (actionType === 'deduct') {
                endpoint = `/api/hr/employees/${userId}/holidays/deduct`;
                payload.days = parseFloat(actionData.days);
            } else {
                endpoint = `/api/hr/employees/${userId}/holidays/adjust`;
                payload.balance = parseFloat(actionData.balance);
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setSnackbar({ open: true, message: 'Holiday action successful', severity: 'success' });
                setActionDialogOpen(false);
                fetchData();
            } else {
                const data = await response.json();
                throw new Error(data.error || 'Action failed');
            }
        } catch (error: any) {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        }
    };

    const openAction = (type: 'add' | 'deduct') => {
        setActionType(type);
        setActionDialogOpen(true);
    };

    if (!passwordVerified) {
        return (
            <PasswordVerificationModal
                open={passwordVerificationOpen}
                onClose={() => setPasswordVerificationOpen(false)}
                onVerify={async (pw) => (await authService.verifyPassword(pw)).data.verified}
                onVerified={() => { grantAccess(); setPasswordVerificationOpen(false); }}
                title="Access HR Management"
                description="Verify password to access HR features."
            />
        );
    }

    if (loading) return <LoadingAnimation />;
    if (!user) {
        return (
            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Typography sx={{ color: 'error.main', fontWeight: 600 }}>User not found</Typography>
            </Box>
        );
    }

    const panelSx = {
        borderRadius: 1,
        boxShadow: 'none',
        border: `1px solid ${panelBorder}`,
        bgcolor: surfaceBg
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minHeight: { xs: 'auto', md: 'calc(100vh - 128px)' }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/hr/management')}
                    sx={{ ...outlinedButtonSx(isDark, panelBorder), px: 1.5 }}
                >
                    Back to Staff List
                </Button>
            </Box>

            <Paper sx={{ ...panelSx, p: { xs: 1.5, sm: 2 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: { md: 240 } }}>
                        <UserAvatar name={user.name} src={user.profile_picture} sx={{ width: 56, height: 56 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: ink, fontSize: '1.25rem' }}>
                                {user.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: muted, wordBreak: 'break-all' }}>
                                {user.mail}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(4, minmax(0, 1fr))'
                            },
                            gap: 1.5
                        }}
                    >
                        <FormControl fullWidth size="small">
                            <InputLabel>Department</InputLabel>
                            <Select
                                value={user.department ?? ''}
                                label="Department"
                                onChange={(e) => handleUpdateProfile({ department: e.target.value as number })}
                                MenuProps={getZoomCompensatedSelectMenuProps()}
                            >
                                {departments.map(d => (
                                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            size="small"
                            label="Weekly hours"
                            type="number"
                            value={user.weekly_hours ?? 40}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setUser(prev => prev ? { ...prev, weekly_hours: isNaN(val) ? 40 : val } : null);
                            }}
                            onBlur={() => handleUpdateProfile({ weekly_hours: user.weekly_hours })}
                            inputProps={{ step: 0.5, min: 1, max: 80 }}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Paid leave / month"
                            type="number"
                            value={user.paid_leave_per_month ?? 0}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setUser(prev => prev ? { ...prev, paid_leave_per_month: isNaN(val) ? 0 : val } : null);
                            }}
                            onBlur={() => handleUpdateProfile({ paid_leave_per_month: user.paid_leave_per_month })}
                            inputProps={{ step: 0.1, min: 0 }}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Birthday"
                            type="date"
                            value={user.birthday || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setUser(prev => prev ? { ...prev, birthday: val } : null);
                            }}
                            onBlur={() => handleUpdateProfile({ birthday: user.birthday })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </Box>
            </Paper>

            <Paper
                sx={{
                    ...panelSx,
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'stretch',
                    gap: { xs: 2, md: 0 }
                }}
            >
                {balances.map((b) => {
                    const awaitingDays = b.category === 'rr'
                        ? leaveAwaitingDeduction.rr
                        : leaveAwaitingDeduction.paid_leave;
                    const showAwaiting = b.category === 'paid_leave' || awaitingDays > 0;
                    return (
                        <Box
                            key={b.category}
                            sx={{
                                flex: '1 1 180px',
                                minWidth: { xs: '100%', sm: 180 },
                                pr: { md: 2.5 },
                                mr: { md: 2.5 },
                                borderRight: { md: `1px solid ${panelBorder}` }
                            }}
                        >
                            <Typography sx={sectionLabelSx(isDark)}>
                                {categoryLabel(b.category)}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontFamily: TABLE_FONT_FAMILY,
                                    fontSize: { xs: '1.75rem', sm: '2rem' },
                                    fontWeight: 600,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.15,
                                    fontVariantNumeric: 'tabular-nums',
                                    color: b.balance < 0 ? 'error.main' : ink
                                }}
                            >
                                {b.balance}
                                <Box component="span" sx={{ ml: 0.75, fontSize: '0.85rem', fontWeight: 500, color: muted }}>
                                    days
                                </Box>
                            </Typography>
                            {showAwaiting && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1.25, maxWidth: 280 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: muted }}>
                                            Awaiting deduction
                                        </Typography>
                                        <Tooltip title={AWAITING_TOOLTIP}>
                                            <InfoOutlined sx={{ fontSize: 16, color: muted, cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: TABLE_FONT_FAMILY,
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: awaitingDays > 0 ? 'warning.main' : muted
                                        }}
                                    >
                                        {awaitingDays} days
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    );
                })}
                <Box
                    sx={{
                        flex: '1 1 200px',
                        minWidth: { xs: '100%', sm: 200 },
                        pr: { md: 2.5 },
                        mr: { md: 2.5 },
                        borderRight: { md: `1px solid ${panelBorder}` }
                    }}
                >
                    <Typography sx={sectionLabelSx(isDark)}>Next posting</Typography>
                    <Typography
                        sx={{
                            mt: 0.5,
                            fontWeight: 600,
                            fontSize: '1.125rem',
                            letterSpacing: '-0.01em',
                            color: ink
                        }}
                    >
                        {nextHolidayCutoff().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: muted, mt: 0.75, maxWidth: 280 }}>
                        Monthly credit {user.paid_leave_per_month ?? 0} days posts with awaiting deductions.
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: { xs: '1 1 100%', md: '0 0 auto' },
                        display: 'flex',
                        flexDirection: { xs: 'row', md: 'column' },
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        gap: 1,
                        minWidth: { md: 148 }
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => openAction('add')}
                        sx={sandButtonSx}
                    >
                        Add
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Remove />}
                        onClick={() => openAction('deduct')}
                        sx={outlinedButtonSx(isDark, panelBorder)}
                    >
                        Deduct
                    </Button>
                </Box>
            </Paper>

            <Box
                sx={{
                    flex: 1,
                    minHeight: { xs: 0, lg: 0 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(260px, 320px)' },
                    gridTemplateRows: { md: 'minmax(280px, 1fr)' },
                    gap: 1.5,
                    alignItems: 'stretch'
                }}
            >
                <Paper
                    sx={{
                        ...panelSx,
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: { xs: 280, md: 0 },
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${panelBorder}`
                        }}
                    >
                        <Typography sx={sectionLabelSx(isDark)}>Holiday history</Typography>
                        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
                            <InputLabel>View</InputLabel>
                            <Select
                                value={historyScope}
                                label="View"
                                onChange={(e) => setHistoryScope(e.target.value as 'in_payroll_period' | 'all' | 'recent')}
                                MenuProps={getZoomCompensatedSelectMenuProps()}
                            >
                                <MenuItem value="in_payroll_period">In payroll period</MenuItem>
                                <MenuItem value="recent">Recent updates (30 days)</MenuItem>
                                <MenuItem value="all">All transactions</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {isMobile ? (
                        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {filteredTransactions.map(t => (
                                <Box
                                    key={t.id}
                                    sx={{
                                        border: `1px solid ${panelBorder}`,
                                        borderRadius: 1,
                                        p: 1.25,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontFamily: TABLE_FONT_FAMILY, fontSize: '0.8125rem', color: ink }}>
                                            {new Date(t.date).toLocaleDateString()}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: TABLE_FONT_FAMILY,
                                                fontWeight: 600,
                                                fontVariantNumeric: 'tabular-nums',
                                                color: t.days < 0 ? 'error.main' : ink
                                            }}
                                        >
                                            {t.days > 0 ? `+${t.days}` : t.days}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={t.type}
                                        size="small"
                                        variant="outlined"
                                        sx={{ alignSelf: 'flex-start', borderRadius: 1, borderColor: panelBorder }}
                                    />
                                    <Typography variant="caption" sx={{ color: muted }}>
                                        Balance after {t.balance_after}
                                        {t.notes ? ` · ${t.notes}` : ''}
                                    </Typography>
                                </Box>
                            ))}
                            {filteredTransactions.length === 0 && (
                                <Box sx={{ py: 6, textAlign: 'center' }}>
                                    <Typography sx={{ fontWeight: 600, color: ink }}>No transactions for this view</Typography>
                                    <Typography variant="body2" sx={{ color: muted, mt: 0.5 }}>
                                        Switch the view or post an add / deduct.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={tableHeadCellSx(isDark, surfaceBg, panelBorder)}>Date</TableCell>
                                        <TableCell sx={tableHeadCellSx(isDark, surfaceBg, panelBorder)}>Type</TableCell>
                                        <TableCell sx={tableHeadCellSx(isDark, surfaceBg, panelBorder)}>Days</TableCell>
                                        <TableCell sx={tableHeadCellSx(isDark, surfaceBg, panelBorder)}>Balance after</TableCell>
                                        <TableCell sx={tableHeadCellSx(isDark, surfaceBg, panelBorder)}>Notes</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredTransactions.map((t, idx) => (
                                        <TableRow
                                            key={t.id}
                                            hover
                                            sx={{
                                                '& td': { borderBottom: `1px solid ${alpha(panelBorder, 0.65)}` },
                                                bgcolor: idx % 2 === 0 ? 'transparent' : alpha(NAUTILUS.deepOcean, isDark ? 0.12 : 0.02)
                                            }}
                                        >
                                            <TableCell sx={{ fontFamily: TABLE_FONT_FAMILY }}>{new Date(t.date).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={t.type}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1, borderColor: panelBorder }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontFamily: TABLE_FONT_FAMILY,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    color: t.days < 0 ? 'error.main' : ink
                                                }}
                                            >
                                                {t.days > 0 ? `+${t.days}` : t.days}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: TABLE_FONT_FAMILY, fontVariantNumeric: 'tabular-nums' }}>
                                                {t.balance_after}
                                            </TableCell>
                                            <TableCell sx={{ color: muted }}>{t.notes}</TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredTransactions.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ border: 0, height: 200 }}>
                                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                                    <Typography sx={{ fontWeight: 600, color: ink }}>No transactions for this view</Typography>
                                                    <Typography variant="body2" sx={{ color: muted, mt: 0.5 }}>
                                                        Switch the view or post an add / deduct.
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                <Paper
                    sx={{
                        ...panelSx,
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: { xs: 180, md: 0 },
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${panelBorder}`
                        }}
                    >
                        <Typography sx={sectionLabelSx(isDark)}>Documents</Typography>
                        {uploading && <CircularProgress size={18} sx={{ color: NAUTILUS.sand }} />}
                    </Box>

                    <Box
                        {...getRootProps()}
                        sx={{
                            mx: 1.5,
                            mt: 1.5,
                            border: '1px dashed',
                            borderColor: isDragActive ? NAUTILUS.sand : panelBorder,
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            bgcolor: isDragActive ? alpha(NAUTILUS.sand, 0.08) : 'transparent',
                            '&:hover': {
                                borderColor: NAUTILUS.sand,
                                bgcolor: alpha(NAUTILUS.sand, isDark ? 0.08 : 0.12)
                            }
                        }}
                    >
                        <input {...getInputProps()} />
                        <CloudUpload sx={{ fontSize: 20, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: ink, fontWeight: 600 }}>
                                {isDragActive ? 'Drop files here' : 'Drop files or click'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: muted }}>
                                PDF, DOC, JPG, PNG, TXT
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {documents.map(d => (
                            <Box
                                key={d.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    border: `1px solid ${panelBorder}`,
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.75
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: ink }}>
                                        {d.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: muted }}>
                                        {d.document_type} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}
                                    </Typography>
                                </Box>
                                <Tooltip title="Download">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDownloadDocument(d.id, d.original_filename)}
                                        sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}
                                    >
                                        <Download fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete document">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteDocument(d.id)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                        {documents.length === 0 && (
                            <Typography variant="body2" sx={{ color: muted, textAlign: 'center', py: 2 }}>
                                No documents
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Box>

            <Dialog
                open={actionDialogOpen}
                onClose={() => setActionDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                        bgcolor: surfaceBg,
                        border: `1px solid ${panelBorder}`,
                        boxShadow: 'none'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5, fontWeight: 600, letterSpacing: '-0.01em', color: ink }}>
                    {actionType === 'add' && 'Add holiday days'}
                    {actionType === 'deduct' && 'Deduct holiday days'}
                    {actionType === 'adjust' && 'Adjust balance'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={actionData.category}
                                label="Category"
                                onChange={(e) => setActionData({ ...actionData, category: e.target.value })}
                                MenuProps={getZoomCompensatedSelectMenuProps()}
                            >
                                <MenuItem value="paid_leave">Paid Leave</MenuItem>
                                <MenuItem value="rr">R&R</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Date"
                            type="date"
                            value={actionData.date}
                            onChange={(e) => setActionData({ ...actionData, date: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        {actionType === 'adjust' ? (
                            <TextField
                                label="New Balance"
                                type="number"
                                value={actionData.balance}
                                onChange={(e) => setActionData({ ...actionData, balance: e.target.value })}
                                fullWidth
                            />
                        ) : (
                            <TextField
                                label="Days"
                                type="number"
                                value={actionData.days}
                                onChange={(e) => setActionData({ ...actionData, days: e.target.value })}
                                fullWidth
                            />
                        )}
                        <TextField
                            label="Reason"
                            value={actionData.reason}
                            onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 2 }}>
                    <Button onClick={() => setActionDialogOpen(false)} sx={{ borderRadius: 1, px: 3, color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleHolidayAction} sx={sandButtonSx}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%', borderRadius: 1, border: `1px solid ${panelBorder}` }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HRUserDetailsPage;
